'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { api } from '@/lib/api'
import { ArrowLeft, Upload, CheckCircle, AlertTriangle, FileSpreadsheet, Download } from 'lucide-react'
import Link from 'next/link'

interface PreviewRow {
  name: string; barcode?: string; ras_mel: number; prix_vente: number
  prix_min: number; stock: number; stock_alert: number; valid: boolean; error?: string
}

export default function ImportPage() {
  const { session } = useApp()
  const router = useRouter()
  const [preview, setPreview] = useState<PreviewRow[]>([])
  const [importing, setImporting] = useState(false)
  const [done, setDone] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    if (session.user.role !== 'patron') router.replace('/dashboard')
  }, [session])

  function parseCSV(text: string): PreviewRow[] {
    const lines = text.trim().split('\n').filter(Boolean).slice(1)
    return lines.map(line => {
      const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g,''))
      const [name, barcode, ras_mel_s, prix_vente_s, prix_min_s, stock_s, stock_alert_s] = cols
      const ras_mel = parseFloat(ras_mel_s)||0, prix_vente = parseFloat(prix_vente_s)||0
      const prix_min = parseFloat(prix_min_s)||0, stock = parseInt(stock_s)||0, stock_alert = parseInt(stock_alert_s)||5
      let error = ''
      if (!name) error = 'Nom manquant'
      else if (ras_mel <= 0) error = 'Ras mel invalide'
      else if (prix_vente <= 0) error = 'Prix vente invalide'
      else if (prix_min <= 0) error = 'Prix min invalide'
      else if (prix_min > prix_vente) error = 'Prix min > prix vente'
      return { name, barcode, ras_mel, prix_vente, prix_min, stock, stock_alert, valid: !error, error }
    })
  }

  function handleFile(file: File) {
    const reader = new FileReader()
    reader.onload = e => setPreview(parseCSV(e.target?.result as string))
    reader.readAsText(file, 'UTF-8')
  }

  async function importAll() {
    const valid = preview.filter(r => r.valid)
    if (!valid.length) return
    setImporting(true)
    await api.importArticles(valid.map(r => ({ shop_id: session!.shop.id, ...r })))
    setImporting(false); setDone(true)
  }

  function downloadTemplate() {
    const csv = `name,barcode,ras_mel,prix_vente,prix_min,stock,stock_alert\n"TV Samsung 55",8801643035845,1200.000,1500.000,1350.000,10,2\n"Frigo Ariston 300L",,850.000,1100.000,950.000,5,1`
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    a.download = 'template_articles.csv'; a.click()
  }

  const validCount = preview.filter(r => r.valid).length

  if (done) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
        <CheckCircle className="w-10 h-10 text-green-500" />
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">{validCount} articles importés! ✅</h2>
      <Link href="/articles" className="w-full max-w-sm py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg text-center block mt-6">
        Voir les articles →
      </Link>
    </div>
  )

  return (
    <div className="min-h-screen pb-8">
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/articles" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">Import CSV</h1>
          <button onClick={downloadTemplate} className="flex items-center gap-1 text-indigo-600 text-sm font-medium">
            <Download className="w-4 h-4" /> Template
          </button>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-700">
          <p className="font-semibold mb-1">Format CSV:</p>
          <p className="font-mono text-xs bg-blue-100 rounded p-2">name, barcode, ras_mel, prix_vente, prix_min, stock, stock_alert</p>
          <button onClick={downloadTemplate} className="text-blue-600 font-semibold mt-2 inline-flex items-center gap-1 text-xs">
            <Download className="w-3 h-3" /> Télécharger le template
          </button>
        </div>

        {preview.length === 0 ? (
          <div onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f) }}
            onDragOver={e => { e.preventDefault(); setDragOver(true) }} onDragLeave={() => setDragOver(false)}
            className={`border-2 border-dashed rounded-3xl p-10 text-center ${dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
            <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium mb-4">Déposer le fichier CSV ici</p>
            <label className="cursor-pointer px-5 py-3 bg-indigo-600 text-white rounded-2xl font-semibold inline-block">
              Choisir un fichier
              <input type="file" accept=".csv,.txt" className="hidden" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
            </label>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex gap-3">
                <span className="text-green-600 font-bold">{validCount} OK</span>
                {preview.filter(r=>!r.valid).length > 0 && <span className="text-red-500 font-bold">{preview.filter(r=>!r.valid).length} erreurs</span>}
              </div>
              <button onClick={() => setPreview([])} className="text-gray-500 text-sm">Recommencer</button>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {preview.slice(0,100).map((row, i) => (
                <div key={i} className={`rounded-2xl p-3 border ${row.valid ? 'bg-white border-gray-100' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm truncate flex-1">{row.name}</span>
                    {row.valid ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                  </div>
                  {row.valid
                    ? <p className="text-xs text-gray-400 mt-0.5">RM:{row.ras_mel} | Vente:{row.prix_vente} | Stock:{row.stock}</p>
                    : <p className="text-xs text-red-500 mt-0.5">{row.error}</p>
                  }
                </div>
              ))}
            </div>
            {validCount > 0 && (
              <button onClick={importAll} disabled={importing}
                className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50 flex items-center justify-center gap-2">
                {importing ? <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Upload className="w-5 h-5" /> Importer {validCount} articles</>}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
